import { useMemo, useState } from 'react';
import { FiCamera, FiPlus, FiTrash2 } from 'react-icons/fi';
import inquiryIllustration from '../../assets/inquiry.png';
import InquiryModal from '../inquiry/InquiryModal';
import { useLanguage } from '../../context/LanguageContext';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(Number(value) || 0);

const InquiryPage = () => {
  const [items, setItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  const { t } = useLanguage();

  const total = useMemo(
    () => items.reduce((sum, item) => sum + (Number(item.price) || 0), 0),
    [items]
  );

  const openModalForNew = () => {
    setEditingItem(null);
    setModalOpen(true);
  };

  const openModalForEdit = (item) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const handleSaveFromModal = (payload) => {
    const normalized = {
      id: payload.item.id ?? Date.now(),
      text: payload.item.text,
      price: payload.item.price,
      image: payload.item.image,
      productType: payload.productType,
      description: payload.description,
    };

    setItems((previous) => {
      const exists = previous.some((item) => item.id === normalized.id);
      if (exists) {
        return previous.map((item) =>
          item.id === normalized.id ? normalized : item
        );
      }
      return [normalized, ...previous];
    });
  };

  const handleRemove = (id) => {
    setItems((previous) => previous.filter((item) => item.id !== id));
  };

  const handleProceedToCheckout = () => {
    if (!items.length) {
      setSubmitMessage({ type: 'error', text: t('inquiry.message_add_item') });
      return;
    }

    if (typeof window === 'undefined') return;

    try {
      setSubmitting(true);
      setSubmitMessage(null);
      window.sessionStorage.setItem(
        'pendingInquiryItems',
        JSON.stringify(items)
      );
      window.location.assign('/checkout-inquiry');
    } catch (error) {
      setSubmitMessage({
        type: 'error',
        text: error?.message || t('inquiry.message_checkout_error'),
      });
      setSubmitting(false);
    }
  };

  return (
    <div className="inquiry">
      <section className="inquiry__hero">
        <img
          src={inquiryIllustration}
          alt={t('inquiry.hero_alt')}
          onError={(event) => {
          }}
        />
        <div>
          <h1>{t('inquiry.hero_title')}</h1>
          <p>{t('inquiry.hero_description')}</p>
        </div>
      </section>

      <section className="inquiry-card">
        <header className="inquiry-card__header">
          <div>
            <p className="eyebrow">{t('inquiry.order_label')}</p>
    
          </div>
    
        </header>
      
        <div className="inquiry-quick" onClick={openModalForNew}>
          <span className="inquiry-quick__icon">
            <FiCamera />
          </span>
          <span className="inquiry-quick__placeholder">
            {t('inquiry.quick_placeholder')}
          </span>
      
        </div>
      </section>

      {items.length > 0 && (
        <section className="inquiry-card inquiry-items">
          <header className="inquiry-card__header">
            <h3>{t('inquiry.items_title')}</h3>
            <p>
              {items.length}{' '}
              {t(items.length === 1 ? 'inquiry.item_single' : 'inquiry.item_plural')}
            </p>
          </header>
          <div className="inquiry-items__list">
            {items.map((item) => (
              <article key={item.id} className="inquiry-item">
                <button
                  type="button"
                  className="inquiry-item__media"
                  onClick={() => openModalForEdit(item)}
                >
                  {item.image ? (
                    <img src={item.image} alt={item.text} />
                  ) : (
                    <FiCamera />
                  )}
                </button>
                <div className="inquiry-item__details" onClick={() => openModalForEdit(item)}>
                  <p className="inquiry-item__name">{item.text}</p>
                  <p className="inquiry-item__meta">
                    {formatCurrency(item.price)}{' '}
                    {item.productType?.label ? `· ${item.productType.label}` : ''}
                  </p>
                  <p className="inquiry-item__description">{item.description}</p>
                </div>
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => handleRemove(item.id)}
                  aria-label={t('inquiry.remove_item')}
                >
                  <FiTrash2 />
                </button>
              </article>
            ))}
          </div>
        
        </section>
      )}

      {submitMessage && (
        <p
          className={`inquiry__message inquiry__message--${submitMessage.type}`}
        >
          {submitMessage.text}
        </p>
      )}

{items.length > 0 &&<div className="inquiry__actions">
        <button
          type="button"
          className="button"
          onClick={handleProceedToCheckout}
          disabled={submitting}
        >
          {submitting ? t('inquiry.opening_checkout') : t('inquiry.confirm_button')}
        </button>
      </div>}

      <InquiryModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveFromModal}
        initialData={
          editingItem
            ? {
                item: editingItem,
                productType: editingItem.productType,
                description: editingItem.description,
              }
            : {}
        }
      />
    </div>
  );
};

export default InquiryPage;
